import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getChallenge, startChallengeDebate, getMe } from "@/lib/api";

const AI_AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/b9a6f8ac-ae9e-4cbf-9b6d-e590a64b44ee/images/6c99ee1aa365db3f6f9a5be21aaf930265acd3722b4b50b28b7389055d758d68.png";

export default function ChallengePage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [ch, me] = await Promise.allSettled([
          getChallenge(challengeId),
          getMe(),
        ]);
        if (ch.status === "fulfilled") setChallenge(ch.value);
        else {
          toast.error("Desafío no encontrado.");
          navigate("/", { replace: true });
          return;
        }
        if (me.status === "fulfilled") setUser(me.value);
      } finally {
        setLoading(false);
      }
    })();
  }, [challengeId, navigate]);

  const handleAccept = async () => {
    if (!user) {
      // Save the pending challenge so OnboardingPage can redirect back after login
      try { localStorage.setItem("pending_challenge", challengeId); } catch (_e) {}
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
      return;
    }
    setStarting(true);
    try {
      const debate = await startChallengeDebate(challengeId, challenge.difficulty_id);
      navigate("/game", {
        state: {
          debate,
          user,
          challenge: {
            challenge_id: challengeId,
            challenger_name: challenge.challenger_name,
            challenger_score: challenge.challenger_score,
            stance: challenge.stance,
            category_label: challenge.category_label,
            cached_rounds_count: challenge.cached_rounds_count,
            max_rounds: challenge.max_rounds,
          },
        },
      });
    } catch (e) {
      toast.error("No se pudo iniciar el desafío. Intentá de nuevo.");
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-display text-2xl uppercase">Cargando desafío...</div>
      </div>
    );
  }

  if (!challenge) return null;

  const scoreColor =
    challenge.challenger_score >= 75
      ? "#A8FF76"
      : challenge.challenger_score >= 65
      ? "#FFDE59"
      : "#FF4C4C";

  const roundsLabel =
    challenge.max_rounds === 1
      ? "1 ronda express · 60 seg"
      : `${challenge.max_rounds} rondas`;

  if (challenge.completed && challenge.opponent_name) {
    const oppWon = (challenge.opponent_score ?? 0) > challenge.challenger_score;
    return (
      <div className="min-h-screen px-4 py-10 flex flex-col items-center justify-center gap-6">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="font-display text-3xl md:text-4xl uppercase tracking-tight text-center"
        >
          Desafío completado
        </motion.div>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-lg border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
        >
          <div className="bg-[#101010] px-5 py-3 font-mono-cm text-[11px] uppercase tracking-widest text-white/70">
            Comparación justa · mismos contraataques de REBECA
          </div>
          <div className="grid grid-cols-2 divide-x-4 divide-[#101010]">
            <div className={`p-5 text-center ${!oppWon ? "bg-[#A8FF76]" : "bg-white"}`}>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-1">
                {challenge.challenger_name?.split(" ")[0]}
              </div>
              <div className="font-display text-5xl tabular-nums">{challenge.challenger_score}</div>
            </div>
            <div className={`p-5 text-center ${oppWon ? "bg-[#FF4C4C]/20" : "bg-white"}`}>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-1">
                {challenge.opponent_name?.split(" ")[0]}
              </div>
              <div className="font-display text-5xl tabular-nums">{challenge.opponent_score}</div>
            </div>
          </div>
          <p className="p-4 text-center font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">
            &ldquo;{challenge.stance}&rdquo;
          </p>
        </motion.div>
        <button
          onClick={() => navigate(user ? "/game" : "/")}
          className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]/40 hover:text-[#101010]"
        >
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-10 flex flex-col items-center justify-center gap-6">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="font-display text-3xl md:text-5xl uppercase tracking-tight text-center"
      >
        TE DESAFIARON
      </motion.div>

      {/* Challenger card */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="w-full max-w-lg bg-[#FFDE59] border-4 border-[#101010] rounded-xl cm-shadow p-6 md:p-8"
      >
        {/* Challenger info */}
        <div className="flex items-center gap-3 mb-5">
          {challenge.challenger_picture ? (
            <img
              src={challenge.challenger_picture}
              alt={challenge.challenger_name}
              referrerPolicy="no-referrer"
              className="w-12 h-12 rounded-full border-3 border-[#101010] border-4"
            />
          ) : (
            <div className="w-12 h-12 rounded-full border-4 border-[#101010] bg-white flex items-center justify-center font-display text-xl">
              {challenge.challenger_name?.[0] || "?"}
            </div>
          )}
          <div>
            <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">
              Te desafía
            </div>
            <div className="font-display text-xl uppercase leading-tight">
              {challenge.challenger_name}
            </div>
          </div>
          <div
            className="ml-auto border-4 border-[#101010] rounded-xl px-4 py-2 text-center"
            style={{ backgroundColor: scoreColor }}
          >
            <div className="font-display text-4xl leading-none tabular-nums">
              {challenge.challenger_score}
            </div>
            <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">
              /100
            </div>
          </div>
        </div>

        {/* Topic */}
        <div className="bg-white border-4 border-[#101010] rounded-xl p-4 mb-5">
          <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 mb-1">
            El tema a debatir
          </div>
          <p className="font-display text-lg md:text-xl uppercase leading-tight">
            &ldquo;{challenge.stance}&rdquo;
          </p>
          <div className="mt-2 inline-block bg-[#101010] text-white font-mono-cm text-[10px] uppercase tracking-widest px-2 py-0.5 rounded">
            {challenge.category_label}
          </div>
        </div>

        {challenge.cached_rounds_count > 0 && (
          <div className="bg-[#A8FF76] border-4 border-[#101010] rounded-xl px-4 py-3 mb-5">
            <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/70 mb-1">
              ⚖️ Comparación justa
            </div>
            <p className="text-sm font-medium">
              REBECA usa los mismos contraataques en {challenge.cached_rounds_count} ronda
              {challenge.cached_rounds_count !== 1 ? "s" : ""}. Solo cambia tu argumento y tu score.
            </p>
          </div>
        )}

        {/* REBECA warning */}
        <div className="bg-[#101010] border-4 border-[#101010] rounded-xl p-4 mb-5 flex items-start gap-3">
          <img src={AI_AVATAR} alt="REBECA" className="w-12 h-12 object-contain shrink-0" />
          <div>
            <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FF4C4C] mb-1">
              REBECA va a defender esa postura contra vos
            </div>
            <p className="text-sm font-medium text-white/80">
              {roundsLabel} · necesitás <span className="text-[#A8FF76] font-bold">75+</span> para ganar.{" "}
              {challenge.challenger_score >= 75
                ? `${challenge.challenger_name.split(" ")[0]} ya la venció con ${challenge.challenger_score}. ¿Podés igualarlo?`
                : `${challenge.challenger_name.split(" ")[0]} no pudo ganarle. ¿Vos sí?`}
            </p>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleAccept}
          disabled={starting}
          className="cm-press w-full bg-[#101010] text-white border-4 border-[#101010] rounded-xl px-7 py-4 font-display uppercase text-xl cm-shadow disabled:opacity-60"
        >
          {starting ? "Iniciando..." : user ? "Aceptar el desafío →" : "Iniciar sesión y aceptar →"}
        </button>

        {!user && (
          <p className="text-center font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/40 mt-3">
            Necesitás cuenta para aceptar desafíos
          </p>
        )}
      </motion.div>

      {/* Back */}
      <button
        onClick={() => navigate("/")}
        className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010]/40 hover:text-[#101010] transition-colors"
      >
        ← Volver al inicio
      </button>
    </div>
  );
}
